// inside your component file (TypeScript)
import React, { useMemo } from 'react';
import { CartesianChart, Line } from 'victory-native'; // or the Skia CartesianChart you're using
import { useFont } from '@shopify/react-native-skia';
import { axisDateLabel, ChartDataPoint } from './axisUtils';
import Colors from '../constants/Colors';
import { lineColors } from '../components/Home/HomeGraphLineSelector';

interface Props {
  graphData: ChartDataPoint[][];
  linesShown?: boolean[]; // which extra lines to show
  selectedTileIndex?: number;
  graphIndex?: number;
}

function buildMergedData(graphData: ChartDataPoint[][]) {
  const maxLen = Math.max(...graphData.map((g) => g?.length ?? 0));
  return Array.from({ length: maxLen }).map((_, i) => {
    const row: any = { index: i };

    row.date = graphData[0]?.[i]?.date ?? graphData.find((g) => g?.[i])?.[i]?.date ?? '';

    // main series is the first one (value)
    row.value = graphData[0]?.[i]?.value ?? null;

    // extra series: create keys series1, series2, ...
    for (let s = 1; s < graphData.length; s += 1) {
      row[`series${s}`] = graphData[s]?.[i]?.value ?? null;
    }
    return row;
  });
}

export default function HappyDetailsGraphMobile({
  graphData,
  linesShown = [],
}: Props) {
  // Build merged data and yKeys
  const merged = useMemo(() => buildMergedData(graphData), [graphData]);

  // yKeys must match the properties created in buildMergedData
  // 'value' is series 0; 'series1' is graphData[1], etc.
  const extraCount = Math.max(0, graphData.length - 1);
  const yKeys = useMemo(() => {
    const keys = ['value']; // primary line
    for (let s = 1; s <= extraCount; s += 1) keys.push(`series${s}`);
    return keys;
  }, [extraCount]);

  // eslint-disable-next-line global-require
  const font = useFont(require('../assets/fonts/Roboto-Regular.ttf'), 14);
  if (!font) { return null; }

  return (
    <CartesianChart
      data={merged}
      xKey="index"
      yKeys={yKeys}
      domain={{ y: [0, 100] }} // set appropriate y domain or compute from data
      padding={{
        top: 30, bottom: 10, left: 10, right: 10,
      }}
      axisOptions={{
        font,
        tickCount: { x: 4, y: 5 },
        // formatXLabel receives the numeric index (because xKey = 'index')
        formatXLabel: (labelIndex: number) => (
          axisDateLabel(merged as unknown as ChartDataPoint[], labelIndex)),
        lineColor: Colors.light.hereworksWhite500,
        lineWidth: { grid: { x: 0, y: 1 }, frame: 0 },
        labelOffset: { x: 0, y: 22 },
      }}
    >
      {({ points }) => (
          <>
            {points.value && (
              <Line
                points={points.value}
                color={Colors.light.hereworksPurple500}
                strokeWidth={1.5}
              />
            )}
            {yKeys.slice(1).map((key, idx) => {
              const seriesIndex = idx + 1; // key === `series${seriesIndex}`
              const show = !!linesShown[seriesIndex];
              if (!show) return null;
              const pts = (points as any)[key];
              if (!pts) return null;
              return (
                <Line
                  key={key}
                  points={pts}
                  color={lineColors[seriesIndex] ?? Colors.light.hereworksDarkBlue200}
                  strokeWidth={1.5}
                />
              );
            })}
          </>)
      }
    </CartesianChart>
  );
}
